import { convexQuery } from "@convex-dev/react-query";
import { useQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { Clock, CreditCard, DollarSign } from "lucide-react";
import { useMemo } from "react";

import {
  employeesGetAll,
  locationSalesGetEmployeeEarningsByWeek,
} from "~/api-refs";
import { StatCard } from "~/components/reports/stat-card";
import { LoadingPanel } from "~/components/shared/loading-panel";
import { Combobox } from "~/components/ui/combobox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { toConvexTableId } from "~/lib/convex-table-id";
import { optionalSearchString } from "~/lib/parse-enum-value";
import { useSearchParamsDateRange } from "~/utils/search-params-date-range";

export const Route = createFileRoute(
  "/_authenticated/reports/employee-earnings-summary"
)({
  component: EmployeeEarningsSummaryPage,
  validateSearch: (search: Record<string, unknown>) => ({
    location: optionalSearchString(search.location),
    employeeId: optionalSearchString(search.employeeId),
  }),
});

function EmployeeEarningsSummaryPage() {
  const navigate = useNavigate();
  const searchParams = useSearch({ strict: false });
  const locationId = searchParams.location;

  const { data: employeesResult } = useQuery(
    convexQuery(employeesGetAll, {
      paginationOpts: { numItems: 500, cursor: null },
    })
  );
  const employees =
    employeesResult?.page.map((e) => ({
      id: e.id,
      firstName: e.firstName,
      lastName: e.lastName,
    })) ?? [];
  const employeeId = searchParams.employeeId;

  if (!locationId) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-center">
          <h3 className="subtitle text-foreground mb-2">Location Required</h3>
          <p className="body text-muted-foreground">
            Please select a location from the filters above to view Employee
            Earnings Summary.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="subtitle text-foreground mb-2">
          Employee Earnings Summary
        </h2>
        <p className="body text-muted-foreground">
          View detailed employee earnings and time tracking data
        </p>
      </div>

      <div className="flex items-center gap-4">
        <Combobox
          className="bg-background h-9 w-48 min-w-0 rounded-md p-2"
          dropdownClassName="w-48"
          items={employees.map((e) => ({
            label: `${e.firstName} ${e.lastName}`,
            value: e.id,
            keywords: [e.firstName, e.lastName],
          }))}
          onValueChange={(v) => {
            void navigate({
              from: "/reports/employee-earnings-summary",
              search: (prev) => ({
                ...prev,
                employeeId: v ?? undefined,
              }),
            });
          }}
          placeholder="Select employee..."
          value={employeeId ?? null}
        />
      </div>

      {employeeId ? (
        <EmployeeEarningsSummaryContent
          employeeId={employeeId}
          locationId={locationId}
        />
      ) : (
        <div className="flex h-32 items-center justify-center">
          <div className="text-center">
            <h3 className="subtitle text-foreground mb-2">Select Employee</h3>
            <p className="body text-muted-foreground">
              Choose an employee from the dropdown above to view their earnings
              summary.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

const range0to6 = Array.from({ length: 7 }, (_, index) => index);

const EmployeeEarningsSummaryContent = ({
  employeeId,
  locationId,
}: {
  employeeId: string;
  locationId: string;
}) => {
  const [dateRange] = useSearchParamsDateRange();

  // Convert date range to year/week for the API
  const { year, week } = useMemo(() => {
    const startDate = dateRange.from ?? new Date();
    const startYear = startDate.getFullYear();
    const firstOfJanuary = new Date(startYear, 0, 1);
    const startWeek = Math.ceil(
      (startDate.getTime() - firstOfJanuary.getTime()) / 86_400_000 / 7
    );
    return { year: startYear, week: startWeek };
  }, [dateRange.from]);

  const { data } = useQuery(
    convexQuery(locationSalesGetEmployeeEarningsByWeek, {
      year,
      week,
      employeeId: toConvexTableId("employees", employeeId),
      locationId: toConvexTableId("locations", locationId),
    })
  );
  const tableCells = useMemo(() => {
    if (!data) {
      return [];
    }
    const cells: string[][] = [];
    cells.push([
      "Clock In",
      ...range0to6.map((i) => data.days[i]?.clockIn ?? "-"),
    ]);
    cells.push([
      "Clock Out",
      ...range0to6.map((i) => data.days[i]?.clockOut ?? "-"),
    ]);
    cells.push([
      "Total Shift Hours",
      ...range0to6.map((i) => data.days[i]?.hoursWorked?.toString() ?? "-"),
    ]);
    if (data.employeePayRate) {
      cells.push([
        "Total Wages",
        ...range0to6.map((i) =>
          data.days[i]
            ? (
                data.days[i].hoursWorked *
                (typeof data.employeePayRate === "string"
                  ? Number.parseFloat(data.employeePayRate)
                  : (data.employeePayRate ?? 0))
              ).toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
              })
            : "-"
        ),
      ]);
    }
    cells.push([
      "Total Tips",
      ...range0to6.map((i) =>
        data.days[i]
          ? (
              data?.days[i]?.cardTips + data.days[i]?.cashTips || 0
            )?.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            })
          : "-"
      ),
    ]);
    return cells;
  }, [data]);
  const totalHours = useMemo(
    () =>
      data?.employeePayRate
        ? Object.values(data.days).reduce(
            (acc, day) => acc + (day?.hoursWorked ?? 0),
            0
          )
        : null,
    [data]
  );
  const totalTips = useMemo(() => {
    if (!data) {
      return null;
    }
    const cardTips = Object.values(data.days).reduce(
      (acc, day) => acc + (day?.cardTips ?? 0),
      0
    );
    const cashTips = Object.values(data.days).reduce(
      (acc, day) => acc + (day?.cashTips ?? 0),
      0
    );
    return {
      cardTips,
      cashTips,
      totalTips: cardTips + cashTips,
    };
  }, [data]);
  if (!data) {
    return <LoadingPanel message="Loading employee earnings data..." />;
  }
  return (
    <>
      <div className="grid grid-cols-3 gap-6" id="employee-earn-summary-top">
        <StatCard
          icon={Clock}
          title="Total Stuff Hours"
          unit="Hours"
          value={totalHours ?? "-"}
        />
        <StatCard
          icon={DollarSign}
          title="Pay Rate"
          unit="USD"
          value={
            data.employeePayRate
              ? (typeof data.employeePayRate === "string"
                  ? Number.parseFloat(data.employeePayRate)
                  : data.employeePayRate
                ).toLocaleString("en-US", {
                  style: "currency",
                  currency: "USD",
                })
              : "-"
          }
        />
        <StatCard
          icon={DollarSign}
          title="Earned Wages"
          unit="USD"
          value={
            totalHours && data.employeePayRate
              ? (
                  totalHours *
                  (typeof data.employeePayRate === "string"
                    ? Number.parseFloat(data.employeePayRate)
                    : data.employeePayRate)
                ).toLocaleString("en-US", {
                  style: "currency",
                  currency: "USD",
                })
              : "-"
          }
        />
      </div>
      <div className="grid grid-cols-3 gap-6" id="employee-earn-summary-bottom">
        <StatCard
          icon={DollarSign}
          title="Cash Tips"
          unit="USD"
          value={
            totalTips?.cashTips.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }) ?? "-"
          }
        />
        <StatCard
          icon={CreditCard}
          title="Card Tips"
          unit="USD"
          value={
            totalTips?.cardTips.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }) ?? "-"
          }
        />
        <StatCard
          icon={DollarSign}
          title="Total Tips"
          unit="USD"
          value={
            totalTips?.totalTips.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }) ?? "-"
          }
        />
      </div>
      <div
        className="border-border flex overflow-x-auto rounded-xl border"
        id="employee-earn-summary-table-container"
      >
        <Table className="w-full" id="employee-earn-summary-table">
          <TableHeader className="bg-muted">
            <TableRow>
              {[
                "",
                "Sunday",
                "Monday",
                "Tuesday",
                "Wednesday",
                "Thursday",
                "Friday",
                "Saturday",
              ].map((day) => (
                <TableHead
                  className="body-sm border-border first:bg-background border-r text-nowrap last:border-none"
                  key={day}
                >
                  {day}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {tableCells.map((row) => (
              <TableRow
                className="group bg-muted odd:bg-background border-b-0"
                key={row[0]}
              >
                {row.map((cell) => (
                  <TableCell
                    className="label border-border text-foreground first:bg-muted group-even:first:bg-muted border-r p-4 text-nowrap last:border-none"
                    key={`${row[0]}-${cell}`}
                  >
                    {cell}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </>
  );
};
